import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { game, PLAYER } from '../game/controller';
import { useGame } from '../game/store';
import { audio } from '../audio/engine';

/**
 * Ghost sightings: a rare, purely visual apparition that drifts through the room in front of
 * the player and out through a wall. It carries no information: it is scheduled at random,
 * never placed relative to the rival, core or caches, and never appears during a threat alert,
 * a cloaked standoff or after the match ends. Atmosphere "off" disables it; "subtle" makes it rarer.
 */

const CHANCE = { normal: 1 / 3, subtle: 1 / 6, off: 0 } as const;
const DURATION_S = 7.5;
const PEAK_OPACITY = 0.12; // top of the brief's 6–12% range to start; tune down after testing
// Bone highlights over soot: the soot reads against pale walls, the bone against dark doorways.
const BONE = 'rgba(216,210,196,';
const SOOT = 'rgba(24,21,18,';
const ink = (r: () => number, a: number) => `${r() < 0.6 ? SOOT : BONE}${a})`;
/** Peak opacity; in development `?ghost=0.6` overrides it to check placement. */
const peak = (): number => {
  if (import.meta.env.DEV) {
    const v = Number(new URLSearchParams(location.search).get('ghost'));
    if (v > 0 && v <= 1) return v;
  }
  return PEAK_OPACITY;
};

/**
 * Placeholder art in the brief's supernatural style: scratchy, smeared ink that thins into wisps.
 * The final figure will be drawn by an illustrator; this only stands in for it.
 */
export function inkFigure(): THREE.CanvasTexture {
  const W = 256, H = 512;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  let seed = 1337;
  const r = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  g.lineCap = 'round';
  // Body: many faint, wobbling strokes that fan out and thin toward the bottom.
  for (let i = 0; i < 260; i++) {
    const t = r();
    const x0 = W / 2 + (r() - 0.5) * 70 * (0.4 + t);
    const y0 = 120 + t * 60;
    g.strokeStyle = ink(r, 0.04 + r() * 0.06);
    g.lineWidth = 1 + r() * 4;
    g.beginPath();
    g.moveTo(x0, y0);
    let x = x0, y = y0;
    const len = 200 + r() * 200;
    for (let s = 0; s < len; s += 12) {
      x += (r() - 0.5) * 9 + (x - W / 2) * 0.02;
      y += 12;
      g.lineTo(x, y);
    }
    g.stroke();
  }
  // Head and shoulders: a denser smudge.
  for (let i = 0; i < 160; i++) {
    const a = r() * Math.PI * 2, d = Math.sqrt(r());
    g.fillStyle = ink(r, 0.03 + r() * 0.06);
    g.beginPath();
    g.ellipse(W / 2 + Math.cos(a) * 34 * d, 86 + Math.sin(a) * 44 * d, 4 + r() * 10, 4 + r() * 10, r() * 3, 0, Math.PI * 2);
    g.fill();
  }
  for (let i = 0; i < 120; i++) {
    g.fillStyle = ink(r, 0.02 + r() * 0.05);
    g.beginPath();
    g.ellipse(W / 2 + (r() - 0.5) * 120, 150 + r() * 40, 6 + r() * 14, 4 + r() * 8, 0, 0, Math.PI * 2);
    g.fill();
  }
  // Hollow eyes: cut two smeared holes out of the head.
  g.globalCompositeOperation = 'destination-out';
  for (const ex of [-13, 13]) {
    for (let k = 0; k < 6; k++) {
      g.fillStyle = 'rgba(0,0,0,0.35)';
      g.beginPath();
      g.ellipse(W / 2 + ex + (r() - 0.5) * 3, 84 + k * 2.5, 6, 7, 0, 0, Math.PI * 2);
      g.fill();
    }
  }
  // Fade everything out toward the bottom so the figure dissolves rather than ends.
  const fade = g.createLinearGradient(0, 200, 0, H);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,1)');
  g.fillStyle = fade;
  g.fillRect(0, 0, W, H);
  g.globalCompositeOperation = 'source-over';
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

interface Plan {
  match: unknown;
  at: number | null; // performance.now() when to try, or null for "not this match"
  done: boolean;
}

export function Ghost() {
  const { camera } = useThree();
  const mesh = useRef<THREE.Mesh>(null);
  const material = useMemo(
    () => new THREE.MeshBasicMaterial({ map: inkFigure(), color: 0xffffff, transparent: true, opacity: 0, depthWrite: false, fog: true }),
    [],
  );
  const plan = useRef<Plan>({ match: null, at: null, done: true });
  const flight = useRef<{ start: number; from: THREE.Vector3; to: THREE.Vector3 } | null>(null);
  const tmp = useMemo(() => ({ fwd: new THREE.Vector3(), right: new THREE.Vector3(), up: new THREE.Vector3() }), []);

  useFrame(() => {
    const m = game.match;
    const el = mesh.current;
    if (!m || !el) return;
    const now = performance.now();
    const s = useGame.getState();

    // A new match: decide once whether this one gets a ghost, and when.
    if (plan.current.match !== m) {
      // Development only: ?ghost makes one appear a moment after the match starts.
      const forced = import.meta.env.DEV && new URLSearchParams(location.search).has('ghost');
      const lucky = forced || Math.random() < CHANCE[s.atmosphere];
      const delay = forced ? 1500 : (45 + Math.random() * 195) * 1000;
      plan.current = { match: m, at: lucky ? now + delay : null, done: !lucky };
      flight.current = null;
      el.visible = false;
    }

    const f = flight.current;
    if (f) {
      const k = (now - f.start) / (DURATION_S * 1000);
      const blocked = s.phase !== 'playing' || s.atmosphere === 'off';
      if (k >= 1 || blocked) {
        flight.current = null;
        el.visible = false;
        return;
      }
      el.position.lerpVectors(f.from, f.to, k);
      el.quaternion.copy(camera.quaternion); // always faces the viewer; "up" follows the player's up
      material.opacity = peak() * Math.sin(Math.PI * k) ** 1.5;
      return;
    }

    const p = plan.current;
    if (p.done || p.at === null || now < p.at) return;
    const quiet = s.phase === 'playing' && s.threatBand === 'none' && !m.viewFor(PLAYER).cloaked && s.atmosphere !== 'off';
    if (!quiet) {
      p.at = now + 5000; // try again shortly
      return;
    }
    // Cross the room in front of the player, from one side wall to the other, slightly off level.
    camera.getWorldDirection(tmp.fwd);
    tmp.up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    tmp.right.crossVectors(tmp.fwd, tmp.up).normalize();
    const side = Math.random() < 0.5 ? 1 : -1;
    // Head near eye level: the figure's centre sits a little below the camera.
    const centre = camera.position.clone().addScaledVector(tmp.fwd, 1.4 + Math.random() * 0.3).addScaledVector(tmp.up, -0.5 + (Math.random() - 0.5) * 0.2);
    flight.current = {
      start: now,
      from: centre.clone().addScaledVector(tmp.right, -2.8 * side),
      to: centre.clone().addScaledVector(tmp.right, 2.8 * side).addScaledVector(tmp.up, 0.25),
    };
    p.done = true;
    el.visible = true;
    material.opacity = 0;
    if (Math.random() < 0.4) audio.playDread(Math.random() < 0.5 ? 'breath' : 'whisper'); // often silent
  });

  return (
    <mesh ref={mesh} visible={false} material={material} renderOrder={5}>
      <planeGeometry args={[1.1, 2.2]} />
    </mesh>
  );
}
